import { useState, useRef } from 'react';
import axios from 'axios';
import { Plus, Edit, Trash2, Check, Sparkles, Send, Upload, FileText, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '../ui/dialog';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { toast } from 'sonner';
import { RichTextEditor } from '../RichTextEditor';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function WritingsManager({ writings, onRefresh, headers }) {
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editItem, setEditItem] = useState(null);
    const [formatting, setFormatting] = useState(false);
    const [publishing, setPublishing] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [uploadedFileName, setUploadedFileName] = useState('');
    const fileInputRef = useRef(null);
    const [formData, setFormData] = useState({ 
        title: '', 
        content: '', 
        content_type: 'direct',
        substack_url: '',
        doc_content: '',
        excerpt: '', 
        image_url: '',
        read_time: '5 min read', 
        published: false 
    });

    const handleSave = async () => {
        try {
            if (editItem) {
                await axios.put(`${API}/writings/${editItem.id}`, formData, { headers });
                toast.success('Writing updated!');
            } else {
                await axios.post(`${API}/writings`, formData, { headers });
                toast.success('Writing created!');
            }
            setDialogOpen(false);
            setEditItem(null);
            setUploadedFileName('');
            setFormData({ title: '', content: '', content_type: 'direct', substack_url: '', doc_content: '', excerpt: '', image_url: '', read_time: '5 min read', published: false });
            onRefresh();
        } catch (error) {
            toast.error('Failed to save');
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this writing?')) return;
        try {
            await axios.delete(`${API}/writings/${id}`, { headers });
            toast.success('Writing deleted!');
            onRefresh();
        } catch (error) {
            toast.error('Failed to delete');
        }
    };

    const handleFileUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const ext = file.name.split('.').pop().toLowerCase();
        if (!['pdf', 'docx', 'doc'].includes(ext)) {
            toast.error('Only PDF and DOCX files are supported');
            return;
        }

        setUploading(true);
        const uploadData = new FormData();
        uploadData.append('file', file);

        try {
            const res = await axios.post(`${API}/writings/extract-file`, uploadData, {
                headers: {
                    ...headers,
                    'Content-Type': 'multipart/form-data'
                }
            });

            setUploadedFileName(res.data.filename);
            setFormData({
                ...formData,
                content: res.data.content,
                content_type: 'direct' // Switch to direct mode with the extracted content
            });

            // Calculate read time (avg 200 words per minute)
            const words = res.data.word_count || 0;
            const readTime = Math.max(1, Math.ceil(words / 200));
            setFormData(prev => ({
                ...prev,
                content: res.data.content,
                content_type: 'direct',
                read_time: `${readTime} min read`
            }));

            toast.success(`Extracted ${res.data.word_count} words from ${file.name}`);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to extract content from file');
        }
        setUploading(false);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleFormat = async () => {
        const textToFormat = formData.content_type === 'doc' ? formData.doc_content : formData.content;
        if (!textToFormat.trim()) {
            toast.error('No text to format');
            return;
        }

        setFormatting(true);
        try {
            const res = await axios.post(`${API}/writings/format`, {
                text: textToFormat,
                content_type: formData.content_type
            }, { headers });

            if (formData.content_type === 'doc') {
                setFormData({ ...formData, doc_content: res.data.formatted });
            } else {
                setFormData({ ...formData, content: res.data.formatted });
            }
            toast.success('Text formatted with AI!');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to format text');
        }
        setFormatting(false);
    };

    const handlePublishToN8n = async (writingId) => {
        setPublishing(writingId);
        try {
            const res = await axios.post(`${API}/writings/${writingId}/publish-webhook`, {}, { headers });
            toast.success(res.data.message);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to publish to n8n');
        }
        setPublishing(null);
    };

    const openEdit = (item) => {
        setEditItem(item);
        setFormData({ 
            title: item.title, 
            content: item.content || '', 
            content_type: item.content_type || 'direct',
            substack_url: item.substack_url || '',
            doc_content: item.doc_content || '',
            excerpt: item.excerpt, 
            image_url: item.image_url || '',
            read_time: item.read_time, 
            published: item.published 
        });
        setDialogOpen(true);
    };

    const getContentTypeLabel = (type) => {
        switch(type) {
            case 'substack': return 'Substack';
            case 'doc': return 'Document';
            default: return 'Written';
        }
    };

    return (
        <div className="retro-card p-6" data-testid="writings-manager">
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-lg uppercase tracking-tight" style={{ fontFamily: "'Silkscreen', cursive" }}>
                    Manage Writings
                </h2>
                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                    <DialogTrigger asChild>
                        <Button className="rounded-none border-2 border-primary bg-primary text-primary-foreground retro-btn text-xs uppercase tracking-widest" data-testid="add-writing-btn">
                            <Plus className="h-4 w-4 mr-2" />
                            Add Writing
                        </Button>
                    </DialogTrigger>
                    <DialogContent className="rounded-none border-2 max-w-2xl max-h-[90vh] overflow-y-auto">
                        <DialogHeader>
                            <DialogTitle className="uppercase tracking-tight" style={{ fontFamily: "'Silkscreen', cursive" }}>
                                {editItem ? 'Edit Writing' : 'New Writing'}
                            </DialogTitle>
                            <DialogDescription>Fill in the details below</DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4">
                            <div>
                                <Label className="text-xs uppercase tracking-widest">Title</Label>
                                <Input className="rounded-none border-2 mt-1" value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} data-testid="writing-title-input" />
                            </div>
                            <div>
                                <Label className="text-xs uppercase tracking-widest">Excerpt (Short description)</Label>
                                <Input className="rounded-none border-2 mt-1" value={formData.excerpt} onChange={(e) => setFormData({...formData, excerpt: e.target.value})} data-testid="writing-excerpt-input" />
                            </div>
                            
                            {/* Content Type Selection */}
                            <div>
                                <Label className="text-xs uppercase tracking-widest">Content Type</Label>
                                <div className="flex gap-2 mt-2">
                                    <Button 
                                        type="button"
                                        variant={formData.content_type === 'direct' ? 'default' : 'outline'}
                                        className="rounded-none border-2 text-xs"
                                        onClick={() => setFormData({...formData, content_type: 'direct'})}
                                    >
                                        Write Content
                                    </Button>
                                    <Button 
                                        type="button"
                                        variant={formData.content_type === 'substack' ? 'default' : 'outline'}
                                        className="rounded-none border-2 text-xs"
                                        onClick={() => setFormData({...formData, content_type: 'substack'})}
                                    >
                                        Substack Link
                                    </Button>
                                    <Button 
                                        type="button"
                                        variant={formData.content_type === 'doc' ? 'default' : 'outline'}
                                        className="rounded-none border-2 text-xs"
                                        onClick={() => setFormData({...formData, content_type: 'doc'})}
                                    >
                                        Paste Document
                                    </Button>
                                    <Button 
                                        type="button"
                                        variant={formData.content_type === 'upload' ? 'default' : 'outline'}
                                        className="rounded-none border-2 text-xs"
                                        onClick={() => setFormData({...formData, content_type: 'upload'})}
                                    >
                                        <Upload className="h-3 w-3 mr-1" />
                                        Upload File
                                    </Button>
                                </div>
                            </div>

                            {/* Conditional Content Input */}
                            {formData.content_type === 'direct' && (
                                <div>
                                    <Label className="text-xs uppercase tracking-widest mb-2 block">Content</Label>
                                    {uploadedFileName && (
                                        <p className="text-xs text-primary mb-2 flex items-center gap-1">
                                            <FileText className="h-3 w-3" />
                                            Extracted from: {uploadedFileName}
                                        </p>
                                    )}
                                    <RichTextEditor 
                                        content={formData.content}
                                        onChange={(html) => setFormData({...formData, content: html})}
                                        placeholder="Start writing your masterpiece..."
                                    />
                                    <Button 
                                        type="button"
                                        variant="outline"
                                        onClick={handleFormat}
                                        disabled={formatting || !formData.content.trim()}
                                        className="mt-2 rounded-none border-2 text-xs uppercase tracking-widest"
                                        data-testid="format-content-btn"
                                    >
                                        <Sparkles className="h-4 w-4 mr-2" />
                                        {formatting ? 'Formatting...' : 'AI Format & Polish'}
                                    </Button>
                                </div>
                            )}

                            {formData.content_type === 'upload' && (
                                <div>
                                    <Label className="text-xs uppercase tracking-widest mb-2 block">Upload PDF or DOCX</Label>
                                    <div className="border-2 border-dashed border-border p-6 text-center">
                                        <input
                                            type="file"
                                            accept=".pdf,.docx,.doc"
                                            onChange={handleFileUpload}
                                            className="hidden"
                                            id="file-upload"
                                            ref={fileInputRef}
                                            data-testid="writing-file-upload"
                                        />
                                        <label htmlFor="file-upload" className="cursor-pointer">
                                            <div className="flex flex-col items-center gap-2">
                                                {uploading ? (
                                                    <>
                                                        <Loader2 className="h-8 w-8 text-primary animate-spin" />
                                                        <p className="text-sm text-muted-foreground">Extracting content...</p>
                                                    </>
                                                ) : (
                                                    <>
                                                        <Upload className="h-8 w-8 text-muted-foreground" />
                                                        <p className="text-sm text-muted-foreground">
                                                            Click to upload PDF or DOCX
                                                        </p>
                                                        <p className="text-xs text-muted-foreground">
                                                            Text will be extracted and added to the editor
                                                        </p>
                                                    </>
                                                )}
                                            </div>
                                        </label>
                                    </div>
                                    {uploadedFileName && (
                                        <div className="mt-3 p-3 bg-muted/50 border-2 border-border">
                                            <p className="text-xs text-primary flex items-center gap-2">
                                                <FileText className="h-4 w-4" />
                                                <span className="font-medium">{uploadedFileName}</span>
                                                <span className="text-muted-foreground">- Content extracted! Switch to "Write Content" to edit.</span>
                                            </p>
                                        </div>
                                    )}
                                </div>
                            )}

                            {formData.content_type === 'substack' && (
                                <div>
                                    <Label className="text-xs uppercase tracking-widest">Substack URL</Label>
                                    <Input className="rounded-none border-2 mt-1" value={formData.substack_url} onChange={(e) => setFormData({...formData, substack_url: e.target.value})} placeholder="https://yourname.substack.com/p/your-post" data-testid="writing-substack-input" />
                                    <p className="text-xs text-muted-foreground mt-1">Visitors will be redirected to your Substack post</p>
                                </div>
                            )}

                            {formData.content_type === 'doc' && (
                                <div>
                                    <Label className="text-xs uppercase tracking-widest mb-2 block">Document Content</Label>
                                    <RichTextEditor 
                                        content={formData.doc_content}
                                        onChange={(html) => setFormData({...formData, doc_content: html})}
                                        placeholder="Paste your document content here..."
                                    />
                                    <Button 
                                        type="button"
                                        variant="outline"
                                        onClick={handleFormat}
                                        disabled={formatting || !formData.doc_content.trim()}
                                        className="mt-2 rounded-none border-2 text-xs uppercase tracking-widest"
                                        data-testid="format-doc-btn"
                                    >
                                        <Sparkles className="h-4 w-4 mr-2" />
                                        {formatting ? 'Formatting...' : 'AI Format & Polish'}
                                    </Button>
                                    <p className="text-xs text-muted-foreground mt-1">Copy and paste content from your Word doc or Google Doc</p>
                                </div>
                            )}

                            {/* Format Before Publishing Section */}
                            {(formData.content_type === 'direct' || formData.content_type === 'doc') && (formData.content.trim() || formData.doc_content.trim()) && (
                                <div className="p-4 bg-primary/5 border-2 border-primary/20">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <Label className="text-xs uppercase tracking-widest flex items-center gap-2">
                                                <Sparkles className="h-4 w-4 text-primary" />
                                                Format Before Publishing
                                            </Label>
                                            <p className="text-xs text-muted-foreground mt-1">
                                                AI will fix grammar, improve flow, and polish your writing
                                            </p>
                                        </div>
                                        <Button 
                                            type="button"
                                            onClick={handleFormat}
                                            disabled={formatting}
                                            className="rounded-none border-2 border-primary bg-primary text-primary-foreground text-xs uppercase tracking-widest"
                                            data-testid="format-before-publish-btn"
                                        >
                                            {formatting ? (
                                                <>
                                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                                    Formatting...
                                                </>
                                            ) : (
                                                <>
                                                    <Sparkles className="h-4 w-4 mr-2" />
                                                    Format Now
                                                </>
                                            )}
                                        </Button>
                                    </div>
                                </div>
                            )}

                            <div className="flex gap-4">
                                <div className="flex-1">
                                    <Label className="text-xs uppercase tracking-widest">Read Time</Label>
                                    <Input className="rounded-none border-2 mt-1" value={formData.read_time} onChange={(e) => setFormData({...formData, read_time: e.target.value})} />
                                </div>
                                <div className="flex items-center gap-2 pt-6">
                                    <Switch checked={formData.published} onCheckedChange={(checked) => setFormData({...formData, published: checked})} data-testid="writing-published-switch" />
                                    <Label className="text-xs uppercase tracking-widest">Published</Label>
                                </div>
                            </div>

                            <div>
                                <Label className="text-xs uppercase tracking-widest">Image URL (optional)</Label>
                                <Input className="rounded-none border-2 mt-1" value={formData.image_url} onChange={(e) => setFormData({...formData, image_url: e.target.value})} placeholder="https://example.com/image.jpg" data-testid="writing-image-url-input" />
                                <p className="text-xs text-muted-foreground mt-1">Featured image for the writing card</p>
                            </div>
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => { setDialogOpen(false); setEditItem(null); }} className="rounded-none border-2">Cancel</Button>
                            <Button onClick={handleSave} className="rounded-none border-2 border-primary bg-primary text-primary-foreground" data-testid="save-writing-btn">Save</Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>

            {writings.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">No writings yet. Create your first one!</p>
            ) : (
                <div className="space-y-4">
                    {writings.map(item => (
                        <div key={item.id} className="flex items-center justify-between p-4 border-2 border-border" data-testid={`writing-row-${item.id}`}>
                            <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                    <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 bg-muted">{getContentTypeLabel(item.content_type)}</span>
                                    <h3 className="font-bold">{item.title}</h3>
                                </div>
                                <p className="text-sm text-muted-foreground">{item.excerpt}</p>
                                {item.content_type === 'substack' && item.substack_url && (
                                    <p className="text-xs text-primary mt-1 truncate">{item.substack_url}</p>
                                )}
                            </div>
                            <div className="flex items-center gap-2">
                                {item.published ? (
                                    <span className="text-xs uppercase tracking-widest text-green-500 flex items-center gap-1"><Check className="h-3 w-3" /> Live</span>
                                ) : (
                                    <span className="text-xs uppercase tracking-widest text-muted-foreground">Draft</span>
                                )}
                                {item.published && (
                                    <Button 
                                        variant="outline" 
                                        size="sm" 
                                        onClick={() => handlePublishToN8n(item.id)} 
                                        disabled={publishing === item.id}
                                        className="rounded-none border-2 text-xs"
                                        data-testid={`publish-n8n-${item.id}`}
                                    >
                                        <Send className="h-3 w-3 mr-1" />
                                        {publishing === item.id ? 'Sending...' : 'n8n'}
                                    </Button>
                                )}
                                <Button variant="outline" size="icon" onClick={() => openEdit(item)} className="rounded-none border-2" data-testid={`edit-writing-${item.id}`}><Edit className="h-4 w-4" /></Button>
                                <Button variant="outline" size="icon" onClick={() => handleDelete(item.id)} className="rounded-none border-2 text-destructive" data-testid={`delete-writing-${item.id}`}><Trash2 className="h-4 w-4" /></Button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
